/**
 * AUTOMATION TYPES — allineati al contratto REST (rest-openapi.yaml, tag
 * Automations / `AutomationsResourceImpl.java`).
 *
 * Il modello UI rispecchia il DTO `AutomationDefinition` (aggregato profondo:
 * `conditionGroups` -> `conditions`, `actions`). Scelte di boundary:
 * - `status` e' un int32 nel DTO (enum `AutomationDefinitionStatus`): qui e'
 *   una union string, convertita ai boundary in `automationMappers.ts`
 *   (pattern taskMappers).
 * - `automationCategoryName` e' denormalizzato server-side dal
 *   `automationCategoryId` (dual-field come Person.personSource): l'elenco lo
 *   usa direttamente per il sottotitolo, senza join con le categorie.
 * - `className` e' il FQN esposto (la label di dominio la risolve il client).
 * - `fieldType` e `valueSource` (condizioni) e `onError` (azioni) sono NOMI di
 *   enum (string) sia nel DTO sia in UI: nessuna conversione ai boundary, union
 *   TS 1:1 con i nomi Java. Le etichette da mostrare le risolve il client con le
 *   proprie chiavi i18n — il Java non le porta.
 * - Le stringhe di config (`eventConfig`/`value`/`config`) sono JSON grezzo.
 * - Le statistiche di esecuzione NON stanno sull'entita': arrivano da un
 *   endpoint separato (`AutomationStats`, GET /automation/{id}/stats).
 */

import type { BaseEntityState, AuditFields } from '@crm/shared';

/**
 * Stato operativo dell'automazione — SYNC con `AutomationDefinitionStatus.java`
 * (DRAFT=1, ACTIVE=2, SUSPENDED=3, ARCHIVED=4). Mappa int<->string in
 * `automationMappers.ts`.
 */
export type AutomationStatus = 'draft' | 'active' | 'suspended' | 'archived';

/**
 * Colore del chip di stato (vocabolario MUI Chip `color`), condiviso da elenco e gallery template.
 * Sta qui, accanto al tipo, per non esportare una costante da un file di componente (rompe l'HMR).
 */
export const STATUS_CHIP_COLOR: Record<AutomationStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  suspended: 'warning',
  draft: 'default',
  archived: 'default',
};

/**
 * Come interpretare `AutomationCondition.value` nel confronto — SYNC con
 * `AutomationConditionValueSource.java` (persistito come NOME dell'enum, non int).
 * `STATIC`: letterale digitato dall'utente. `FIELD`: `value` è il `fieldName` di un altro campo
 * del record (confronto campo-a-campo). `EXPRESSION`: espressione calcolata — parte del contratto
 * persistito, ma l'editor NON la produce ancora (nessun linguaggio di espressioni definito).
 */
export type AutomationConditionValueSource = 'STATIC' | 'FIELD' | 'EXPRESSION';

/**
 * Politica di errore di un'azione — SYNC con `AutomationActionOnError.java` (persistito come NOME
 * dell'enum, non int). `STOP`: le azioni successive non partono. `CONTINUE`: partono comunque.
 * `RETRY`: si ritenta (vedi `retryCount`/`retryDelaySeconds`).
 *
 * Nessun mapper ai boundary, a differenza di `AutomationStatus`: lo stesso valore attraversa DB,
 * JSON e UI. Le etichette sono client-side, come per `valueSource`.
 */
export type AutomationActionOnError = 'STOP' | 'CONTINUE' | 'RETRY';

/**
 * Array UI per la Select della modale (label = chiave i18n), come `CALL_DIRECTIONS`.
 *
 * `onError` è comune a OGNI azione, non parte della configurazione specifica di una: quella dipende
 * dal tipo (un invio mail vuole oggetto e corpo), questa no — qualunque cosa faccia, un'azione può
 * fallire. Per questo sta nel blocco comune della modale insieme al titolo, e non nel form che
 * arriverà per-azione.
 */
export const ACTION_ON_ERROR_OPTIONS: { value: AutomationActionOnError; label: string }[] = [
  { value: 'STOP', label: 'automation.form.onErrorStop' },
  { value: 'CONTINUE', label: 'automation.form.onErrorContinue' },
  { value: 'RETRY', label: 'automation.form.onErrorRetry' },
];

/**
 * Sorgenti che il builder sa davvero produrre e serializzare. `EXPRESSION` fa parte del contratto
 * persistito ma nessun widget la genera e nessuna forma di valore la rappresenta (manca il linguaggio di
 * espressioni), quindi la si esclude invece di ridichiarare la union: il giorno che arriverà,
 * togliere l'`Exclude` fa emergere in compilazione tutti i punti da coprire (editor e mapper).
 */
export type EditableValueSource = Exclude<AutomationConditionValueSource, 'EXPRESSION'>;

/** Singola condizione (foglia dell'albero). */
export interface AutomationCondition {
  automationConditionId: number;
  fieldName: string;
  /** Tipo del campo: nome dell'enum `ParamType` lato server (es. "ENUM"). */
  fieldType: AutomationParamType;
  operator: string;
  /** Valore come stringa JSON grezza; con `valueSource: 'FIELD'` è il `fieldName` del campo destro. */
  value: string | null;
  valueSource: AutomationConditionValueSource;
  priority: number;
}

/** Gruppo di condizioni: condizioni in AND dentro il gruppo, gruppi in OR fra loro (struttura fissa,
 *  DNF completa) — nessun operatore di gruppo. */
export interface AutomationConditionGroup {
  automationConditionGroupId: number;
  priority: number;
  conditions: AutomationCondition[];
}

/**
 * Azione da eseguire. `config` e' JSON grezzo.
 *
 * `code` e' il codice del catalogo (`AutomationActionType.code`): stesso valore, stesso nome, da
 * entrambi i lati. `title` e' testo dell'utente — il builder lo pre-riempie con l'etichetta del
 * catalogo quando l'azione si aggiunge, ma da li' in poi e' suo, e per questo si persiste invece di
 * derivarlo. Non e' nullable: il builder lo scrive sempre, e il service lo pretende.
 */
export interface AutomationAction {
  automationActionId: number;
  code: string;
  title: string;
  config: string | null;
  onError: AutomationActionOnError;
  retryCount: number;
  retryDelaySeconds: number;
  priority: number;
  enabled: boolean;
}

/** Modello UI dell'automazione — rispecchia il DTO `AutomationDefinition`. */
export interface Automation extends AuditFields {
  automationDefinitionId: number;
  name: string;
  description: string | null;
  /** FQN della classe di dominio (es. it.dvel.crm.core.model.Person). */
  className: string;
  automationCategoryId: number;
  /** Nome categoria denormalizzato dal server; null se non risolvibile. */
  automationCategoryName: string | null;
  eventType: string;
  eventConfig: string | null;
  /**
   * Se l'automazione riscatta finché la condizione dura. Riguarda i soli eventi schedulati
   * (`AutomationEvent.scheduled`): false = una volta per episodio, true = a ogni passata del job.
   */
  repeatable: boolean;
  /**
   * Quanto storico tenere: `true` registra ogni record esaminato, compresi quelli su cui
   * l'automazione non ha agito (non idonei, già trattati, fermati dall'anti-loop); `false` tiene i
   * soli record su cui ha agito. Non cambia cosa l'automazione fa, cambia quanto se ne legge nel
   * monitoraggio. Sui TEMPLATE non ha significato — non girano, quindi non producono storico — e
   * il builder non lo mostra.
   */
  logEveryRecord: boolean;
  priority: number;
  template: boolean;
  status: AutomationStatus;
  version: number;
  mainAutomationDefinitionId: number;
  conditionGroups: AutomationConditionGroup[];
  actions: AutomationAction[];
}

/** Categoria (read-only, per il dropdown del builder e il chip colorato). */
export interface AutomationCategory {
  automationCategoryId: number;
  name: string;
  /** Colore del testo del chip (es. #ffffff), applicato inline. Obbligatorio server-side. */
  fontColor: string;
  /** Colore di sfondo del chip (es. #ff9800), applicato inline. Obbligatorio server-side. */
  backColor: string;
  priority: number;
}

// ─── Catalogo QUANDO (GET /automations/object-types) ───────────────────
// Read-only, globale (non per-sito), già in forma finale: nessun mapper DTO↔UI
// (a differenza di Automation.status). Etichette localizzate lato server nel
// locale dell'utente. SYNC con la SPI catalogo (crm-automation-api, package
// `catalog`): AutomationObjectType / AutomationEventType / ParamType.

/**
 * Tipo scalare di base di un parametro evento / campo condizione — guida editor e validazione.
 * SYNC con `ParamType.java` (INTEGER/DECIMAL/MONEY/DATE rangeable + BOOLEAN/STRING + ENUM;
 * "range" è un flag su ParamDescriptor, non un tipo). Su `ENUM` l'editor mostra una tendina
 * alimentata da `CatalogField.enumValues`, sia che il campo si filtri sia che si scriva.
 */
export type AutomationParamType =
  | 'INTEGER'
  | 'DECIMAL'
  | 'MONEY'
  | 'DATE'
  | 'BOOLEAN'
  | 'STRING'
  | 'ENUM';

/**
 * Descrittore di un parametro configurabile, di un evento (terzo slot del QUANDO) o di un'azione
 * (blocco ALLORA). Guida il form-renderer (`ParamField`); il valore inserito viene serializzato
 * in `AutomationDefinition.eventConfig` per un evento e in `AutomationAction.config` per
 * un'azione, con la stessa forma.
 */
export interface AutomationParam {
  /** Nome tecnico: chiave nel JSON di `eventConfig` (evento) o di `config` (azione). */
  name: string;
  label: string;
  type: AutomationParamType;
  required: boolean;
  /** true = intervallo from/to (solo per i tipi rangeable). */
  range: boolean;
  /**
   * Valori ammessi sui param `ENUM` (tendina), vuoto sugli altri tipi. Li dichiara l'ENTITÀ e non
   * l'evento — «stato modificato a X» offre gli stati del preventivo o quelli dell'anagrafica a
   * seconda di dove sia dichiarato — e arrivano già risolti nel locale dell'utente, come quelli di
   * un `CatalogField`: è lo stesso vocabolario, servito al QUANDO invece che al SE.
   */
  enumValues: CatalogEnumValue[];
}

/**
 * Evento di attivazione supportato da un oggetto (blocco QUANDO). `code` è il
 * valore persistito in `AutomationDefinition.eventType`.
 */
export interface AutomationEvent {
  code: string;
  label: string;
  /**
   * L'evento nasce da una scansione periodica anziché da un'operazione sul record. È l'unico caso
   * in cui il riscatto (`Automation.repeatable`) ha senso, quindi il builder mostra quella scelta
   * solo qui.
   */
  scheduled: boolean;
  params: AutomationParam[];
}

/**
 * Oggetto automatizzabile del catalogo QUANDO. `classNameId` è il valore da
 * salvare in `AutomationDefinition.classNameId`; `className` è l'FQN esposto.
 */
export interface AutomationObjectType {
  className: string;
  classNameId: number;
  label: string;
  events: AutomationEvent[];
}

// ─── Campi del catalogo ─────────────────────────────────────────────────
// Un campo di un'entità, nella forma in cui il server lo espone a DUE
// cataloghi: i campi FILTRABILI del blocco SE
// (GET /automations/condition-types?className=) e quelli SCRIVIBILI del
// blocco ALLORA (GET /automations/updatable-fields?className=). Read-only,
// per-sito, etichette localizzate lato server. SYNC con la SPI
// `catalog.fields` (crm-automation-api) e con lo schema REST omonimo:
// CatalogField / CatalogEnumValue, stessi nomi da entrambi i lati.

/** Valore ammesso di un {@link CatalogField} di tipo ENUM (tendina dell'editor). */
export interface CatalogEnumValue {
  /** Valore tecnico persistito (es. id numerico dell'enum di dominio, in forma testuale). */
  value: string;
  label: string;
}

/**
 * Arità del valore atteso da un operatore (SYNC con `Cardinality.java`): guida la composizione
 * dell'editor di valore. `SINGLE` = un valore; `RANGE` = coppia from/to (`[from, to]`); `SET` =
 * insieme (`[v1, v2, ...]`); `RELATIVE` = quantità e unità relative a oggi (`[n, unit]`, solo sulle
 * date). È il backend a dichiararla, così il client non deve conoscere i singoli operatori.
 */
export type OperatorCardinality = 'SINGLE' | 'RANGE' | 'SET' | 'RELATIVE';

/**
 * Unità di una quantità relativa a oggi (SYNC con `RelativeUnit.java`), nell'ordine della tendina.
 * Il nome è quello persistito nel valore (`{"value": 30, "unit": "DAY"}`); l'etichetta la mette il
 * client, al plurale nella tendina e accordata con la quantità nel riepilogo.
 */
export const RELATIVE_UNITS = ['DAY', 'MONTH', 'YEAR'] as const;

export type RelativeUnit = (typeof RELATIVE_UNITS)[number];

/** L'unità di una quantità relativa quando il valore non la dice: la stessa del backend. */
export const DEFAULT_RELATIVE_UNIT: RelativeUnit = 'DAY';

/**
 * Operatore di confronto ammesso da un {@link CatalogField} (SYNC con `Operator.java`). `code` è
 * il nome dell'enum persistito in `AutomationCondition.operator`; `label` è già localizzata lato
 * server (la traduzione la pilota il backend, non il client); `cardinality` è l'arità del valore.
 */
export interface ConditionOperator {
  code: string;
  label: string;
  /**
   * Simbolo di confronto (=, ≠, >, ≥, <, ≤), che il riepilogo mostra al posto dell'etichetta per
   * stare compatto. NON è localizzato — un simbolo non si traduce — ed è assente per gli operatori
   * che non ne hanno uno universale (BETWEEN, CONTAINS, NOT_CONTAINS, IN e i relativi a oggi):
   * quelli si dicono a parole, e chi rende ricade sulla `label`.
   */
  symbol?: string | null;
  cardinality: OperatorCardinality;
}

/**
 * Campo di un'entità nel catalogo del builder. `type` guida l'editor di valore (come i param del
 * QUANDO); `enumValues` è valorizzato solo per `type === 'ENUM'`.
 *
 * Serve entrambi gli elenchi — filtrabili (SE) e scrivibili (ALLORA) — perché il server li espone
 * con lo stesso schema: la differenza fra i due è di appartenenza all'elenco, non di forma.
 * L'unica asimmetria è `operators`, che arriva SOLO dal catalogo dei filtrabili: servono a
 * confrontare, e nel blocco ALLORA si assegna. Per questo è opzionale, e chi lo legge deve
 * aspettarsi che manchi.
 */
export interface CatalogField {
  fieldName: string;
  label: string;
  type: AutomationParamType;
  operators?: ConditionOperator[];
  enumValues: CatalogEnumValue[];
}

// ─── Catalogo ALLORA (GET /automations/action-types/{groupId}?className=) ───
// Le azioni eseguibili registrate. Read-only, etichette localizzate lato server.
// SYNC con la SPI `catalog.actions` (crm-automation-api): AutomationActionType.
//
// Il server lo filtra per l'oggetto del QUANDO (`className`, via `isApplicable`),
// e come per gli eventi anche i `params` di un'azione li calcola per sito e oggetto.
// La configurazione si trova in quest'ordine (AutomationActionModal):
// 1. il form scritto a mano per il `code` (actions/automationActionConfig.ts);
// 2. altrimenti i `params` che l'azione dichiara, resi con lo stesso ParamField
//    del QUANDO; il valore sta in `config` con la stessa forma di eventConfig
//    (`{"maxReminders": {"value": 2}}`, `{"from": …, "to": …}` per un intervallo);
// 3. altrimenti «nessun parametro» (`hasConfiguration: false`).

/**
 * Azione eseguibile del catalogo ALLORA. `code` è il valore persistito in
 * `AutomationAction.code`; `label` è già localizzata.
 *
 * Niente `onError`: la politica di errore è una scelta della singola azione *configurata* e vive
 * su `AutomationAction.onError`, non sul tipo.
 */
export interface AutomationActionType {
  code: string;
  label: string;
  /**
   * Riga che spiega cosa fa l'azione, già localizzata: la `label` dice come si chiama, questa che
   * effetto ha, e serve a chi deve SCEGLIERE fra le voci del catalogo.
   *
   * Facoltativa, e il server la omette del tutto quando l'azione non la dichiara — non manda stringa
   * vuota. Chi rende non deve quindi lasciare una riga vuota sotto il nome: o c'è testo, o non c'è
   * l'elemento.
   */
  description?: string;
  /**
   * Token astratto dell'icona (es. `mail`, `task`): non una classe CSS e non il percorso di un
   * file, ma un identificatore che il client risolve nella propria mappa token → componente.
   * Stessa convenzione del campo `icon` di `MenuItem` / `EntityTypeDescriptor` (`@crm/contracts`),
   * e stessa logica della `label`: il catalogo dichiara cosa intende, non come si disegna.
   *
   * Un token sconosciuto ricade sull'icona generica invece di rompere — è ciò che permette a un
   * plugin di dichiarare la propria icona senza che il core conosca il suo `code`. Il token
   * `default` è l'icona generica, e lo restituisce la SPI per chi non ne dichiara una.
   */
  icon: string;
  /**
   * Se l'azione ha parametri da compilare: `hasConfiguration()` lato Java. Sempre presente.
   *
   * `false` distingue "azione senza parametri" da "azione il cui form il client non conosce", che
   * nel registry dei form (`automationActionConfig.ts`) si presentano uguali — nessuna voce. Per le
   * prime la modale dice che non c'è niente da configurare invece di lasciare il vuoto sotto il
   * titolo. Il payload invece non cambia: la config resta `null`, ed è il server ad accettarla e a
   * salvarla come oggetto vuoto (il perché è in `actionsToDto` di `AutomationForm.tsx`).
   */
  hasConfiguration: boolean;
  /**
   * Valori che il server mette a disposizione della UI di configurazione di QUESTA azione, come
   * stringa JSON grezza: `getConfigContext` lato Java. Serve quando la UI deve mostrare dei DATI e
   * non un vocabolario cablabile — le caselle da cui spedire, un importo di riferimento — mentre
   * le sorgenti dei destinatari, che sono un vocabolario finito, restano scritte nel client.
   *
   * Stringa e non oggetto tipizzato, per la stessa ragione di `AutomationAction.config` e in senso
   * opposto: là il client manda al server una configurazione di forma libera, qui il server manda
   * al client dei valori di forma libera. A conoscere la forma sono solo l'azione e la sua UI,
   * quindi il parse lo fa il form di quell'azione e non un mapper comune — descriverla qui sarebbe
   * una promessa che il core non può mantenere per le azioni dei plugin.
   *
   * Facoltativa: il server omette il campo quando l'azione non dichiara nulla, che è la
   * maggioranza. NON è la configurazione salvata — quella è `config` di `AutomationAction`.
   */
  configContext?: string;
  /**
   * Parametri semplici che l'azione dichiara (`getParams` lato Java), vuoto se non ne dichiara. La
   * modale li disegna solo se per il `code` non c'è un form scritto a mano; il valore va in
   * `config[param.name]` con la forma di `eventConfig`.
   */
  params: AutomationParam[];
}

// ─── Validazione (POST /automations/{groupId}/validate) ────────────────
// Problemi che impedirebbero di PUBBLICARE l'automazione. L'endpoint riceve la
// bozza in composizione (stesso payload della create), non persiste nulla e
// risponde sempre 200: lista vuota = pubblicabile. Le regole sono quelle della
// pubblicazione, eseguite e raccolte lato server — non riscritte qui.

/**
 * I tre blocchi di cui è fatta un'automazione. Il backend colloca ogni violazione nel suo, così il
 * pannello non dice soltanto cosa manca ma DOVE manca — e, per differenza, quali blocchi sono a
 * posto. SYNC CROSS-LAYER: 1:1 con le chiavi dell'esito di `AutomationDefinitionValidator.validate`.
 */
export type AutomationBlock = 'WHEN' | 'IF' | 'THEN';

/** Un problema del builder; `message` è già nella lingua dell'utente (risolto lato server). */
export interface AutomationViolation {
  block: AutomationBlock;
  message: string;
}

/**
 * Statistiche di esecuzione aggregate (endpoint /automation/{id}/stats). Sono
 * separate dall'entita': l'elenco le carica on-demand per gli id a video.
 * `runCount` = successi + errori (run completati); `lastRunDate` null se mai.
 */
export interface AutomationStats {
  automationDefinitionId: number;
  runCount: number;
  successCount: number;
  failureCount: number;
  lastRunDate: string | null;
  /** Durata media dei run completati, in NANOSECONDI (KPI "tempo medio"); 0 se nessun run. */
  averageDuration: number;
  /** Totale azioni eseguite (AutomationActionExecution) su tutti i run; sottotitolo del KPI tempo medio. */
  actionExecutionCount: number;
}

/**
 * Esito di un dry-run (endpoint POST /automation/{id}/simulation). Niente di
 * persistito: si calcola a comando e vive quanto la schermata.
 * I conteggi delle due fasi, selezione e valutazione. Azioni previste e campione
 * di righe entreranno qui insieme al motore che li produce — dichiararli ora
 * come opzionali significherebbe pubblicare un contratto che il server non sa
 * onorare.
 * I due si leggono in fila come un imbuto — candidati, poi idonei — e i nomi
 * descrivono lo STATO del record nel percorso, non la fase che lo ha prodotto:
 * "valutati" sarebbe stato il tranello, perché valutati lo sono tutti i
 * candidati e il numero coinciderebbe col primo.
 */
export interface AutomationSimulation {
  /**
   * I record raccolti dalla selezione, cioè i candidati alla valutazione; a
   * video sono i "record intercettati". Nel motore vero a intercettare è il
   * blocco QUANDO; nella simulazione, che il QUANDO non lo rigioca, il suo posto
   * lo prende il campione degli ultimi N record. Può essere MINORE del limite
   * richiesto — pochi record a DB, oppure permessi mancanti — e non è un errore.
   */
  candidateRecords: number;
  /**
   * Quanti candidati superano il blocco SE: su quanti record l'automazione
   * eseguirebbe davvero le azioni. Sempre <= `candidateRecords`. Un'automazione
   * senza condizioni li rende idonei tutti, perché il blocco SE è facoltativo.
   */
  eligibleRecords: number;
  /**
   * Quante esecuzioni di azione ne verrebbero fuori: `eligibleRecords` per il
   * numero di azioni ATTIVE. Previsione e limite superiore, non promessa — in
   * esecuzione un'azione può saltare il proprio record (una mail su un contatto
   * senza indirizzo) e i retry non sono contati.
   */
  plannedActions: number;
  /**
   * Quanto è durata LA SIMULAZIONE: lettura, selezione coi permessi e
   * valutazione. NON è il costo di un'esecuzione vera — il dry-run non esegue
   * azioni, e in un run reale sono invii mail e creazioni attività a dominare il
   * tempo. Dice quanto pesa la simulazione al crescere del campione.
   *
   * In NANOSECONDI, come ogni durata delle automazioni: i nomi non portano il
   * suffisso dell'unità perché ce n'è una sola, dalla singola condizione al run
   * intero.
   */
  simulationDuration: number;
  /**
   * Il record valutato più in fretta. Misura la sola valutazione del blocco SE:
   * la somma dei tre tempi per record NON dà `simulationDuration`, che comprende
   * anche lettura e permessi. 0 su campione vuoto.
   */
  minDuration: number;
  /**
   * Il record valutato più lentamente. Molto sopra il minimo è normale sul primo
   * record, che paga il riscaldamento delle cache dei finder. 0 su campione
   * vuoto.
   */
  maxDuration: number;
  /**
   * Tempo medio per record: totale diviso i record ESAMINATI, non i soli idonei —
   * la valutazione gira su tutti. 0 su campione vuoto.
   *
   * Omonimo di `AutomationStats.averageDuration` ma di tutt'altra cosa: quello è
   * la media dei run VERI, questo della sola valutazione in un dry-run.
   */
  averageDuration: number;
  /**
   * Le intestazioni delle colonne FISSE, già tradotte, nell'ordine in cui il
   * provider dell'entità le dichiara. Sono quelle che dicono QUALE record è —
   * chiave primaria, nome completo, ragione sociale — e vanno rese PRIMA delle
   * `columns` dinamiche, che dicono invece perché è stato intercettato.
   *
   * Nessuna è cablata qui: le decide l'entità, quindi una tabella su Person
   * intesta "Nome completo" dove una su Business intesta "Azienda". Le celle si
   * prendono da `records[].fixedValues` per POSIZIONE, non per nome.
   *
   * VUOTO solo su campione vuoto: senza record non c'è nessuno a cui chiedere le
   * proprie colonne.
   */
  fixedColumns: string[];
  /**
   * Le colonne della tabella: i campi che il blocco SE usa davvero, deduplicati
   * (lo stesso campo in due gruppi resta una colonna) e nell'ordine in cui
   * l'utente ha composto le condizioni. Stesso tipo del catalogo condizioni, e
   * per una ragione precisa: porta `label` già localizzata e, sugli ENUM, gli
   * `enumValues` con cui rendere l'etichetta invece del valore tecnico — senza
   * una seconda chiamata al catalogo. `operators` arriva vuoto, qui non si
   * sceglie nulla.
   *
   * VUOTO se l'automazione non ha un blocco SE (si applica a tutti i record) e
   * anche su campione vuoto, perché le colonne si ricavano dagli esiti.
   */
  columns: CatalogField[];
  /**
   * I record esaminati, TUTTI e non i soli idonei: la tabella mostra anche gli
   * esclusi, che sono quelli su cui c'è qualcosa da capire. Nell'ordine in cui la
   * selezione li ha raccolti, dal più recente per data di creazione.
   */
  records: AutomationSimulationRecord[];
}

/**
 * Una riga della tabella: un record del campione col proprio esito.
 *
 * La tabella NON arriva impaginata dal server. Le celle si compongono unendo
 * `columns` e `conditions` per `fieldName`; il motivo di un'esclusione sono le
 * condizioni con `satisfied` a false, e per dire cosa chiedevano si risale alla
 * configurazione dell'automazione — che il builder ha già in store — tramite
 * `automationConditionId`. Sul filo viaggia quindi solo ciò che il client non
 * può sapere: quali record, con che valori, con che esito.
 */
export interface AutomationSimulationRecord {
  /**
   * I valori delle colonne FISSE per questo record, già composti dal provider
   * dell'entità: nomi uniti, suffissi fra parentesi, numeri resi in stringa. Si
   * accoppiano POSIZIONALMENTE con `fixedColumns` della risposta — stesso
   * indice, stessa colonna — e hanno la stessa lunghezza su ogni riga, perché a
   * dichiarare le colonne è l'entità e non il singolo record.
   *
   * Arrivano PRESENTATI, al contrario di `conditions[].actualValue` che arriva
   * grezzo: qui non c'è niente da formattare, e provarci significherebbe disfare
   * ciò che il provider ha composto.
   */
  fixedValues: string[];
  /**
   * Chiave primaria del record sull'entità dell'automazione. Resta nel DTO anche
   * ora che le colonne fisse la mostrano di norma come prima colonna: quella è
   * una scelta del provider e potrebbe non farla, mentre qui serve come chiave di
   * rendering della riga — che non può dipendere da cosa l'entità decide di
   * mostrare.
   */
  recordId: number;
  /**
   * Se il record supera il blocco SE. È l'OR dei gruppi, vero anche quando di
   * gruppi non ce n'è nessuno.
   *
   * NON ricavabile dalle `conditions` qui sotto: sono in lista piatta e hanno
   * perso il confine fra i rami OR, quindi il loro AND darebbe il verdetto
   * sbagliato su un'automazione con più di un gruppo.
   */
  eligible: boolean;
  /**
   * L'esito di OGNI condizione su questo record, in lista piatta. Il livello
   * gruppo non viaggia perché si ricostruisce da `automationConditionId`. Una
   * condizione messa in due gruppi compare quindi due volte, con lo stesso valore
   * letto. VUOTA se l'automazione non ha condizioni.
   */
  conditions: AutomationSimulationCondition[];
}

/**
 * L'esito di una condizione su un record: se l'ha soddisfatta e con quale valore.
 *
 * PERCHÉ una condizione non ha fatto match non è un messaggio ma un dato — una
 * condizione è `campo OP valore`, e di quei pezzi il client ha già campo,
 * operatore e valore atteso dalla configurazione: l'unico che gli mancava è
 * `actualValue`, il valore letto sul record.
 */
export interface AutomationSimulationCondition {
  /**
   * La condizione di cui questo è l'esito: la chiave per risalire alla
   * configurazione già in store — operatore, valore atteso, gruppo di
   * appartenenza — nulla di cui debba viaggiare una copia per ogni riga.
   */
  automationConditionId: number;
  /** Campo valutato: la chiave con cui la riga si allinea a `columns`. */
  fieldName: string;
  /**
   * Il valore del campo su questo record, GREZZO: numeri in forma decimale, date
   * in ISO (giorno, che è la granularità con cui il confronto decide), ENUM col
   * valore tecnico da risolvere in etichetta con gli `enumValues` della colonna.
   *
   * ASSENTE con due significati, che li distingue `reasonKey`: senza causa la
   * condizione è stata valutata e il campo era davvero vuoto; con la causa, il
   * valore non è stato letto affatto.
   */
  actualValue?: string;
  /**
   * Se il record soddisfa la condizione. FALSO anche quando la condizione non era
   * valutabile — su una regola che non si è capita non si esegue nulla — e a
   * distinguere i due casi è `reasonKey`.
   */
  satisfied: boolean;
  /**
   * Chiave i18n della causa, presente SOLO quando la condizione non si è potuta
   * valutare: entità non più automatizzabile, operatore o origine del valore non
   * più riconosciuti, campo illeggibile. Assente quando la condizione è stata
   * valutata, che il record la soddisfi o no.
   */
  reasonKey?: string;
}

/**
 * Stato di un run — SYNC con `AutomationRunStatus.java` (RUNNING=1, SUCCESS=2,
 * FAILED=3). Mappa int<->string in `automationMappers.ts`.
 */
export type AutomationRunStatus = 'running' | 'success' | 'failed';

/** Un run (esecuzione) di un'automazione — riga del log di monitoraggio. */
export interface AutomationRun extends AuditFields {
  automationRunId: number;
  automationDefinitionId: number;
  startDate: string;
  /** Fine del run; null se ancora in corso. */
  endDate: string | null;
  /** Durata del run in NANOSECONDI, persistita al completamento; 0 se ancora in corso. */
  duration: number;
  processedItems: number;
  /**
   * Se QUESTA passata ha conservato tutti i record esaminati, o i soli record su cui l'automazione
   * ha agito. Copiato dall'automazione all'apertura del run e non riletto da lei: il flag si cambia
   * in-place su un'automazione attiva, quindi quello della definizione dice com'è configurata
   * adesso mentre questo dice com'era quando la passata è girata. Il drill-down lo usa per
   * distinguere "nessun record trattato" da "record esaminati, ma non conservati".
   */
  logEveryRecord: boolean;
  status: AutomationRunStatus;
  errorMessage: string | null;
}

/**
 * Stato dell'esito su UN record — SYNC con `AutomationExecutionStatus.java` (RUNNING=1,
 * SUCCESS=2, FAILED=3, SKIPPED=4, ALREADY_EXECUTED=5, LOOP_PREVENTED=6). Mappa int<->string in
 * `automationMappers.ts`.
 * Non è lo stato del RUN: quello dice se la passata ha avuto guasti, questo che cosa è successo
 * al singolo record. Un run `success` contiene tranquillamente record `skipped`.
 */
export type AutomationExecutionStatus =
  | 'running'
  | 'success'
  | 'failed'
  | 'skipped'
  | 'already-executed'
  | 'loop-prevented';

/**
 * Una colonna con cui si riconosce il record trattato: intestazione GIÀ tradotta dal server e
 * valore GIÀ composto dal provider dell'entità (nome più cognome, ragione sociale più dominio).
 * Il client non traduce e non compone: disegna.
 */
export interface AutomationDisplayValue {
  label: string;
  value: string;
}

/**
 * Che cosa è successo su UN record dentro un run — riga del drill-down del monitoraggio.
 * Senza campi audit, al contrario di `AutomationRun`: un'esecuzione non ha un utente che l'ha
 * creata, e company e sito sono quelli del run.
 */
export interface AutomationExecution {
  automationExecutionId: number;
  automationRunId: number;
  /** Chiave primaria del record trattato, sull'entità dell'automazione. */
  classPK: number;
  /** Mai vuoto: se il record non è più leggibile resta la sola chiave primaria. */
  displayValues: AutomationDisplayValue[];
  status: AutomationExecutionStatus;
  startDate: string;
  /** Fine del trattamento; null se ancora in corso. */
  endDate: string | null;
  /** Durata del trattamento in NANOSECONDI, come tutte le durate del dominio. */
  duration: number;
  /** Causa, dove lo stato ne ha una: guasto sui falliti, motivo sui fermati dall'anti-loop. */
  errorMessage: string | null;
  /**
   * Le condizioni del blocco SE che questo record NON ha soddisfatto: il perché di uno stato
   * `skipped`. Vuoto su ogni altro stato — un record eseguito le condizioni le ha passate, uno
   * fermato dalla catena non è mai arrivato a valutarle.
   * SOLO le cadute, al contrario di `AutomationSimulationRecord.conditions` che le porta tutte: il
   * verdetto persistito scarta le soddisfatte quando scrive. Il gruppo non viaggia e si ricostruisce
   * dalla configurazione, per `automationConditionId`.
   */
  failedConditions: AutomationSimulationCondition[];
}

export interface AutomationState extends BaseEntityState<Automation> {
  totalCount: number;
  /** Categorie del sito (tab + colori chip della gallery template), da `fetchAutomationCategories`. */
  categories: AutomationCategory[];
  /** Template del sito (card della gallery), da `fetchAutomationTemplates`; separati da `items`. */
  templates: Automation[];
  /** Catalogo oggetti automatizzabili del builder (blocco QUANDO), da `fetchAutomationObjectTypes`;
   *  globale (non per-sito). Le due combo del QUANDO leggono da qui. */
  objectTypes: AutomationObjectType[];
  /** Catalogo campi condizione del builder (blocco SE), indicizzato per FQN dell'oggetto, da
   *  `fetchAutomationConditionTypes`. Cache per className: cambiare oggetto non rifetcha se già noto. */
  conditionTypesByClassName: Record<string, CatalogField[]>;
  /** Catalogo campi SCRIVIBILI (blocco ALLORA), indicizzato per FQN dell'oggetto, da
   *  `fetchAutomationUpdatableFields`. Stessa forma e stessa cache dei filtrabili, ma elenco
   *  distinto: alimenterà la configurazione delle azioni di aggiornamento campo. */
  updatableFieldsByClassName: Record<string, CatalogField[]>;
  /** Catalogo azioni del builder (blocco ALLORA), indicizzato per FQN dell'oggetto, da
   *  `fetchAutomationActionTypes`. Indicizzato come il SE e non più lista piatta: il server filtra
   *  le azioni per l'entità, perché non tutte valgono per tutte (aggiornare un campo ha senso solo
   *  dove ce n'è uno scrivibile). Stessa cache per className.
   *  Lo usa il SOLO builder — modale di scelta e icona della riga: la gallery non ne ha bisogno,
   *  perché le card nominano le azioni col titolo che viaggia sull'automazione. */
  actionTypesByClassName: Record<string, AutomationActionType[]>;
  /** Violazioni della bozza in composizione (pannello Validazione), da `validateAutomation`.
   *  Lista vuota = pubblicabile; `null` = non ancora validata (il pannello non si pronuncia). */
  violations: AutomationViolation[] | null;
  /** requestId dell'ultima validazione richiesta: sul `.fulfilled`/`.rejected` si scartano le
   *  risposte stale (il debounce non serializza — due validazioni possono rispondere invertite, e
   *  vincerebbe l'ultima RISPOSTA invece dell'ultima RICHIESTA). */
  validationReqId: string | null;
  /** Statistiche per automazione, popolate on-demand da `fetchAutomationStats`. */
  statsById: Record<number, AutomationStats>;
  /** Log dei run per automazione, popolato on-demand da `fetchAutomationRuns`. */
  runsById: Record<number, AutomationRun[]>;
  /** Esiti per record, indicizzati per RUN e popolati on-demand da `fetchAutomationRunExecutions`:
   *  li chiede la riga del log che si apre, non la pagina, quindi la chiave è il run e non
   *  l'automazione. Un run già aperto una volta non si rifetcha. */
  executionsByRunId: Record<number, AutomationExecution[]>;
  /** requestId dell'ultimo fetch stats/run per automazione: sul `.fulfilled` si scartano le
   *  risposte stale (cambio periodo rapido nel monitoraggio, dove più richieste per lo stesso id
   *  sono in volo e una più vecchia/lenta potrebbe sovrascrivere quella fresca). */
  statsReqById: Record<number, string>;
  runsReqById: Record<number, string>;
  executionsReqByRunId: Record<number, string>;
}

// ─── Payload di input (albero senza id: il server allinea via delete+recreate) ─

export interface AutomationConditionInput {
  fieldName: string;
  fieldType: AutomationParamType;
  operator: string;
  value: string | null;
  valueSource: AutomationConditionValueSource;
  priority: number;
}

export interface AutomationConditionGroupInput {
  priority: number;
  conditions: AutomationConditionInput[];
}

export interface AutomationActionInput {
  /**
   * Identità di PERSISTENZA dell'azione, ed è il campo su cui il server allinea il ramo in update:
   * `> 0` per un'azione già salvata, che si aggiorna in place conservando il proprio id — e con esso
   * lo storico delle esecuzioni che lo referenziano — e `0` per un'azione aggiunta in edit, che si
   * crea. Le azioni esistenti non più presenti nel payload si cancellano.
   *
   * Eccezione al pattern «input senza id» dell'albero: le condizioni non lo portano, e infatti il
   * server le ricrea tutte a ogni salvataggio. Il contratto REST lo dichiara facoltativo — assente
   * vale 0, cioè azione nuova — ma il builder lo manda sempre, e questo tipo descrive ciò che il
   * builder manda.
   */
  automationActionId: number;
  code: string;
  title: string;
  config: string | null;
  onError: AutomationActionOnError;
  retryCount: number;
  retryDelaySeconds: number;
  priority: number;
  enabled: boolean;
}

/**
 * Payload di creazione (POST /automations/{groupId}). Non include i campi
 * gestiti dal server (id, audit, version, mainAutomationDefinitionId, status,
 * automationCategoryName). `status` nasce sempre DRAFT lato server.
 */
export interface AutomationCreatePayload {
  name: string;
  description?: string | null;
  className: string;
  eventType: string;
  automationCategoryId: number;
  priority?: number;
  template?: boolean;
  eventConfig?: string | null;
  repeatable?: boolean;
  logEveryRecord?: boolean;
  conditionGroups?: AutomationConditionGroupInput[];
  actions?: AutomationActionInput[];
}

/** Payload di aggiornamento (PATCH /automation/{id}), parziale. */
export interface AutomationUpdatePayload extends Partial<AutomationCreatePayload> {
  automationDefinitionId: number;
}
